import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import {
    DURATION_UNIT_TRANSLATION_MAP,
    MEMBERSHIP_TEMPLATE_TRANSLATION_MAP,
} from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { pricingPlanMessages } from 'src/features/pricing/messages';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { useListRoles } from 'src/features/role/api/useListRoles';
import { useListSites } from 'src/features/sites/api/useListSites';
import { siteMessages } from 'src/features/sites/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import WizardOverviewSection, { WizardStepInfo } from 'src/registry/ui/Wizard';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { translate } from 'src/utils/translate';

type Props = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
};

type SectionData = {
    title: string;
    value?: string | null;
};

export default function OverviewStep({ form }: Props) {
    const { formatMessage, locale } = useIntl();

    const { parkingId } = useParking();
    const typeValues = form.watch();
    const { data: sitesData } = useListSites({ parkingId });
    const { data: rolesData } = useListRoles({ parkingId });

    const { data: pricingPlansData } = useListPricingPlans({
        parkingId,
        currently_active: true,
        status: PRICING_PLAN_STATUS.PUBLISHED,
    });

    const pricingPlanOptions = React.useMemo(() => {
        if (!pricingPlansData) return [];
        return pricingPlansData.results.map((plan) => {
            const translatedPlan = plan.translations.find((p) => p.language === locale);

            return { value: plan.identity, label: translatedPlan?.name || '' };
        });
    }, [pricingPlansData, locale]);

    const siteOptions = React.useMemo(() => {
        if (!sitesData) return [];
        return sitesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, sitesData]);

    const translationData = typeValues.translations.reduce((acc, translation) => {
        acc.push({
            title: `${formatMessage(commonMessages.name)} (${translation.language.toUpperCase()})`,
            value: translation.name || '-',
        });
        acc.push({
            title: `${formatMessage(generalMessages.description)} (${translation.language.toUpperCase()})`,
            value: translation.description || '-',
        });
        return acc;
    }, [] as SectionData[]);

    const overviewData = React.useMemo(() => {
        return [
            {
                title: formatMessage(membershipTypeMessages.basics),
                data: [
                    ...translationData,
                    {
                        title: formatMessage(commonMessages.type),
                        value: typeValues.category
                            ? formatMessage(MEMBERSHIP_TEMPLATE_TRANSLATION_MAP[typeValues.category])
                            : null,
                    },
                    {
                        title: formatMessage(siteMessages.site),
                        value: siteOptions.find((option) => option.value === typeValues.site)?.label,
                    },
                ],
            },
            {
                title: formatMessage(membershipTypeMessages.access),
                data: [
                    {
                        title: formatMessage(membershipTypeMessages.limitSpaces),
                        value: <BooleanBadge booleanField={typeValues.has_allowed_spaces_count} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.customSpaceCount),
                        value: <BooleanBadge booleanField={typeValues.allow_custom_space_count_definition} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.extraVisitors),
                        value: <BooleanBadge booleanField={typeValues.allow_extra_visitors} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.maxAllowedSpaces),
                        value: typeValues.max_allowed_spaces_count,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.maxExtraVisitors),
                        value: typeValues.max_extra_visitors_count,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.timeRestriction),
                        value: <BooleanBadge booleanField={typeValues.has_time_restriction} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.customTimeRestriction),
                        value: <BooleanBadge booleanField={typeValues.allow_custom_time_restriction} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.allowAccessOverCapacityLimit),
                        value: <BooleanBadge booleanField={typeValues.allow_access_over_capacity_limit} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.allowAccessAsClientOutsideRestrictions),
                        value: <BooleanBadge booleanField={typeValues.allow_access_as_client_outside_restrictions} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.contributeToAvailableSpaces),
                        value: <BooleanBadge booleanField={typeValues.contribute_to_available_spaces} />,
                    },
                ],
            },
            {
                title: formatMessage(membershipTypeMessages.price),
                data: [
                    {
                        title: formatMessage(membershipTypeMessages.hasPricingPlan),
                        value: <BooleanBadge booleanField={typeValues.has_pricing_plan} />,
                    },
                    {
                        title: formatMessage(pricingPlanMessages.pricingPlan),
                        value: pricingPlanOptions.find((option) => option.value === typeValues.pricing_plan_identity)
                            ?.label,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.applyPricingPerSpace),
                        value: <BooleanBadge booleanField={typeValues.apply_pricing_per_space} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.autoRenew),
                        value: <BooleanBadge booleanField={typeValues.auto_renew} />,
                    },
                ],
            },
            {
                title: formatMessage(membershipTypeMessages.validity),
                data: [
                    {
                        title: formatMessage(membershipTypeMessages.limitDuration),
                        value: <BooleanBadge booleanField={typeValues.limit_duration} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.customDuration),
                        value: <BooleanBadge booleanField={typeValues.custom_duration} />,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.duration),
                        value: typeValues.duration,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.durationUnit),
                        value: typeValues.duration_unit
                            ? formatMessage(DURATION_UNIT_TRANSLATION_MAP[typeValues.duration_unit])
                            : null,
                    },
                    {
                        title: formatMessage(membershipTypeMessages.limitToRoles),
                        value: typeValues?.limit_to_roles
                            ?.map((limitRole) => rolesData?.results.find((role) => role.id === limitRole)?.name)
                            .join(', '),
                    },
                ],
            },
        ];
    }, [typeValues, translationData, siteOptions, pricingPlanOptions, rolesData, formatMessage]);

    return (
        <div className="flex gap-4">
            <div className="flex flex-2 flex-col gap-2">
                <div className="columns-1 gap-4">
                    {overviewData.map((data) => (
                        <WizardOverviewSection {...data} key={data.title} />
                    ))}
                </div>
                <div className="bg-primary-50 border-primary-100 flex gap-2 rounded-md border p-2">
                    <FontAwesomeIcon icon={ICON_MAP.CIRCLE_EXCLAMATION_SOLID} className="text-primary-600" />
                    <p className="text-primary-700 text-sm">
                        {formatMessage(membershipTypeMessages.overviewWhatHappensNext, {
                            b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                        })}
                    </p>
                </div>
            </div>
            <div className="flex-1">
                <WizardStepInfo
                    header={formatMessage(membershipTypeMessages.overview)}
                    body={formatMessage(membershipTypeMessages.overviewBody)}
                />
            </div>
        </div>
    );
}
